/**
 * Estate Detail Page — Route Module
 *
 * The Rails-superset detail page (estates-enrichment PR-4): phase
 * switcher, photo mosaic + gallery modal (per-view Google photos, D14), buildings with split counts /
 * POI distances, per-building map markers, in-estate rent & sale rails,
 * district census, similar estates.
 */
import { SaveButton } from '@/shared/components/listings';
import { projectLocalized } from '@letsgethome/lib/i18n';
import { routes } from '@letsgethome/routes';
import { plaintextString } from '@letsgethome/types/branded';
import { PageContainer } from '@letsgethome/ui/page-container';
import { useLoaderData } from 'react-router';

import { Breadcrumbs } from '../../../../shared/components/Breadcrumbs';
import { SeoHead } from '../../../../shared/components/SeoHead';
import { useLocale, useTranslations } from '../../../../shared/i18n';
import { Link } from '../../../../shared/i18n/routing';
import { ListingCarousel } from '../../listings/components/ListingCarousel';
import { BuildingsTable } from '../components/BuildingsTable';
import { CensusSection } from '../components/CensusSection';
import { EstateCard } from '../components/EstateCard';
import { EstateFactsSection } from '../components/EstateFactsSection';
import { EstateFaqSection } from '../components/EstateFaqSection';
import { EstateGallery } from '../components/EstateGallery';
import { EstateMap } from '../components/EstateMap';
import { PhaseSwitcher } from '../components/PhaseSwitcher';
import { PriceTrendSection } from '../components/PriceTrendSection';
import { RecentDealsSection } from '../components/RecentDealsSection';
import type { loader } from './detail.loader';

export function Component() {
  const { estate, seo, faqs } = useLoaderData<typeof loader>();
  const locale = useLocale();
  const t = useTranslations('estates.detail');

  const name = plaintextString(projectLocalized(estate.name, locale));
  const districtName = plaintextString(projectLocalized(estate.districtName, locale));
  const streetAddress = plaintextString(projectLocalized(estate.streetAddress, locale));
  // Per-intent rails from the backend (PR-B1) — the old client-side split
  // of `inEstateListings` let 6 recent rent listings starve the sale rail.
  // `source === 'nearby'` = the Rails-parity fallback (the estate has zero
  // own active listings; cards are within 1.5 km) — heading switches and
  // the see-all link is dropped (it would open an empty estate-scoped list).
  const { rentListings, saleListings } = estate;

  return (
    <>
      {/* The backend's entity JSON-LD, plus the estate `FAQPage` — which rides
          on `faqs`, not on `seo.structuredData`, precisely so that emitting it
          requires having the answers in hand. `<EstateFaqSection>` below
          renders that same value (SEO B.3b). */}
      <SeoHead
        {...seo}
        structuredData={faqs ? [...(seo.structuredData ?? []), faqs.jsonLd] : seo.structuredData}
      />
      <div className="min-h-screen bg-background">
        <PhaseSwitcher phases={estate.phases} locale={locale} />

        {/* Header */}
        <PageContainer size="7xl" className="pt-6 sm:px-6 lg:px-8">
          <Breadcrumbs items={seo.breadcrumbs} />
          <p className="text-sm font-medium text-success-text">
            {districtName}
            {estate.districtHub && (
              <Link
                href={routes.districts.detail(estate.districtHub.slug)}
                prefetch="intent"
                className="ml-2 border-l border-current/40 pl-2 underline-offset-2 hover:underline"
              >
                {plaintextString(estate.districtHub.name)}
              </Link>
            )}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold text-foreground sm:text-4xl">{name}</h1>
            {estate.nearMtr && (
              <span className="rounded-full bg-info/10 px-3 py-1 text-sm font-medium text-info-text">
                {t('nearMtr')}
              </span>
            )}
            {/* Estate-level save heart (PR-B3) — the Rails detail page carried
                a save affordance in the header. */}
            <SaveButton
              saveable={{ kind: 'estate', id: estate.id }}
              serverSaved={estate.saved}
              size="md"
              className="border border-border"
            />
          </div>
          {streetAddress && <p className="mt-2 text-muted-foreground">📍 {streetAddress}</p>}
        </PageContainer>

        {/* D14: Google Places photos are fetched PER VIEW, from the browser,
            and stored nowhere — the SSR HTML carries the skeleton, never a
            Google URL. */}
        <EstateGallery estatePublicId={estate.publicId} name={name} />

        {/* Structural facts (SEO F1) — also the FAQ JSON-LD's answer set */}
        <EstateFactsSection facts={estate.facts} locale={locale} />

        {/* D10: a VILLAGE's blocks are its house numbers, so the server sends
            only their COUNT — no per-house rows, and (below) no per-house
            map pins: the map falls back to the estate's own coordinate. */}
        {estate.villageHouseCount !== undefined ? (
          <PageContainer as="section" size="7xl" className="py-8 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold">
              {t('housesCount', { count: estate.villageHouseCount })}
            </h2>
          </PageContainer>
        ) : (
          <BuildingsTable buildings={estate.buildings} estateId={estate.id} locale={locale} />
        )}

        {/* Own-platform ASKING-price trend (SEO F4). Present only when the
            backend's MIN_MONTHS guard says there is a trend to show. */}
        {estate.priceTrend && <PriceTrendSection trend={estate.priceTrend} locale={locale} />}

        {/* SEO G3 — 成交. Deliberately placed directly BELOW the asking-price
            trend: the two are the most confusable pair on the page, and
            reading them in order (what sellers ask, then what buyers paid) is
            what makes the distinction obvious. Absent when nothing is
            recorded. */}
        {estate.recentDeals.length > 0 && (
          <RecentDealsSection
            deals={estate.recentDeals}
            estateSlug={estate.slug}
            estatePublicId={estate.publicId}
            locale={locale}
          />
        )}

        {/* Location map with per-building markers */}
        {(estate.coordinate || estate.buildings.some((b) => b.coordinate)) && (
          <PageContainer as="section" size="7xl" className="py-8 sm:px-6 lg:px-8">
            <h2 className="mb-6 text-2xl font-bold">{t('location')}</h2>
            <EstateMap
              coordinate={estate.coordinate}
              buildings={estate.buildings}
              locale={locale}
              ariaLabel={t('location')}
              place={{ address: streetAddress, district: districtName }}
            />
          </PageContainer>
        )}

        {/* Rent rail (in-estate, or nearby fallback when the estate has none) */}
        {rentListings.listings.length > 0 && (
          <section className="bg-muted py-12">
            <PageContainer size="7xl" className="sm:px-6 lg:px-8">
              <ListingCarousel
                title={
                  rentListings.source === 'nearby'
                    ? t('nearbyRentListings', { name })
                    : t('rentListings', { name })
                }
                seeAllHref={
                  rentListings.source === 'nearby'
                    ? undefined
                    : routes.listings.rent({ estateId: estate.id })
                }
                listings={rentListings.listings}
                transactionType="rent"
              />
            </PageContainer>
          </section>
        )}

        {/* Sale rail (in-estate, or nearby fallback when the estate has none) */}
        {saleListings.listings.length > 0 && (
          <section className="py-12">
            <PageContainer size="7xl" className="sm:px-6 lg:px-8">
              <ListingCarousel
                title={
                  saleListings.source === 'nearby'
                    ? t('nearbySaleListings', { name })
                    : t('saleListings', { name })
                }
                seeAllHref={
                  saleListings.source === 'nearby'
                    ? undefined
                    : routes.listings.buy({ estateId: estate.id })
                }
                listings={saleListings.listings}
                transactionType="sale"
              />
            </PageContainer>
          </section>
        )}

        {/* District census (2021) */}
        <CensusSection districtName={estate.districtName} locale={locale} />

        {/* The FAQ the `FAQPage` JSON-LD above describes (SEO F3 / B.3b).
            Absent exactly when the backend held no answerable fact — in which
            case no claim was emitted either. */}
        {faqs && <EstateFaqSection faqs={faqs} />}

        {/* Similar estates */}
        {estate.similarEstates.length > 0 && (
          <PageContainer as="section" size="7xl" className="py-8 sm:px-6 lg:px-8">
            <h2 className="mb-6 text-2xl font-bold">{t('similar')}</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {estate.similarEstates.map((similar) => (
                <EstateCard key={similar.id} estate={similar} />
              ))}
            </div>
          </PageContainer>
        )}

        {/* CTA */}
        <PageContainer as="section" size="7xl" className="py-12 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-success to-success/90 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-success-foreground mb-4">
              {t('ctaTitle', { name })}
            </h2>
            <Link
              href={routes.listings.rent({ estateId: estate.id })}
              prefetch="intent"
              className="inline-block bg-background text-success-text font-semibold px-8 py-3 rounded-full hover:bg-background/90 transition-colors"
            >
              {t('ctaButton')}
            </Link>
          </div>
        </PageContainer>
      </div>
    </>
  );
}
